import {useCallback, useEffect, useState} from 'react';
import {clamp} from 'lodash';
import Image from 'next/image';

import {Carousel, Modal, Text} from '#component/base';
import {CarouselProvider} from '#component/provider';
import {useCarousel, useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {Step, StepsCarouselProps} from './KitRegistrationInstructionModal.types';

import * as css from './KitRegistrationInstructionModal.css';

const STEPS: Step[] = [
  {
    heading: 'Take your sample',
    body: () => (
      <Text as="p" color="doveGray">
        Do not eat or drink for 30 minutes before taking the sample. Follow the instructions
        carefully to guarantee an accurate result.
      </Text>
    ),
  },
  {
    body: () => (
      <Text as="p" color="doveGray">
        After removing all contents from the package, open the swab by pulling apart the wrapper at
        the end that says “Peel Here.” Be careful to only touch the plastic handle (and not the soft
        tip of the swab) to prevent sample contamination.
      </Text>
    ),
  },
  {
    body: () => (
      <Text as="p" color="doveGray">
        Rub the soft tip of the swab up and down firmly against the inside of your entire cheek
        15-20 times (with pressure equivalent to brushing your teeth). Rotate the swab tip as you
        are rubbing. Cover your entire cheek from the top to bottom and side to side.
      </Text>
    ),
  },
  {
    body: () => (
      <Text as="p" color="doveGray">
        Unscrew the vial lid and hold steady in one hand. Slowly twist the swab into the vial with a
        corkscrew motion. There will be resistance, but push the swab to the bottom of the tube.
      </Text>
    ),
  },
  {
    body: () => (
      <Text as="p" color="doveGray">
        Move the swab up and down rapidly inside the tube 10-15 times without moving the swab out of
        the liquid.
      </Text>
    ),
  },
  {
    body: () => (
      <Text as="p" color="doveGray">
        Remove the swab by slowly twisting out with a corkscrew motion. Discard the swab. Replace
        the cap on the vial, close tightly, and put the vial in the biohazard bag. Do not place the
        swab in the biohazard bag.
      </Text>
    ),
  },
  {
    body: () => (
      <Text as="p" color="doveGray">
        Put the biohazard bag (with the completed vial inside) back inside the package with the
        prepaid return label. Peel off the adhesive strip protector on the package flap, close, and
        press firmly on the flap to seal the package closed.
      </Text>
    ),
  },
  {
    body: () => (
      <Text as="p" color="doveGray">
        Place the prepaid package in any USPS mailbox or drop off at any USPS location.{' '}
        <Text as="strong" color="doveGray" fontWeight="semiBold">
          It must be a USPS location.
        </Text>
      </Text>
    ),
  },
];

const StepsCarousel = ({onStepIndexChange}: StepsCarouselProps) => {
  const [stepIndex, setStepIndex] = useState<number>(0);
  const {isMobile} = useViewportSize();

  const {
    ref: carouselRef,
    goToNextSlide,
    goToPreviousSlide,
  } = useCarousel({slideWidth: isMobile ? 340 : 450, gap: 24});

  // @ts-ignore
  window.carouselRef = carouselRef;

  const handleCarouselScroll = useCallback(() => {
    const $carousel = carouselRef?.current || null;
    if ($carousel) {
      const slideWidth = $carousel.scrollWidth / $carousel.childNodes.length;
      const slideIndex = clamp(Math.round($carousel.scrollLeft / slideWidth), 0, STEPS.length - 1);

      if (stepIndex !== slideIndex) {
        setStepIndex(slideIndex);
      }
    }
  }, [carouselRef, stepIndex]);

  useEffect(() => {
    const $carousel = carouselRef?.current || null;

    $carousel?.addEventListener('scroll', handleCarouselScroll);
    return () => {
      $carousel?.removeEventListener('scroll', handleCarouselScroll);
    };
  }, [carouselRef, handleCarouselScroll]);

  useEffect(() => {
    if (onStepIndexChange) {
      onStepIndexChange(stepIndex);
    }
  }, [onStepIndexChange, stepIndex]);

  return (
    <div className={sprinkles({overflow: 'hidden', width: 'full'})}>
      <Carousel ref={carouselRef} widths={['100%', '100%', '100%']}>
        {STEPS.map((step, index) => {
          return (
            <div key={index} className={sprinkles({w: 'full'})}>
              <div className={sprinkles({flex: 'flex', dir: 'col', gap: [36, 36, 40]})}>
                <div className={sprinkles({flex: 'flex', dir: 'col', gap: 16})}>
                  {step.heading && (
                    <Text as="h2" variant="heading2" color="burgundy">
                      {step.heading}
                    </Text>
                  )}
                  {step.body()}
                </div>
                <div className={css.imageWrapper}>
                  <Image
                    alt={`Registration instruction step ${index + 1} of ${STEPS.length}`}
                    layout="fill"
                    objectFit="cover"
                    src={`/image/registration/instruction-step-${index + 1}.png`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </Carousel>
      <div
        className={sprinkles({
          flex: 'flex',
          dir: 'row',
          grow: 1,
          content: 'between',
          mT: 48,
          mB: 36,
        })}>
        <button
          aria-label="Previous step"
          className={css.navigationButton}
          disabled={stepIndex === 0}
          onClick={goToPreviousSlide}>
          <svg aria-hidden viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.008 19.412h37.404a.588.588 0 0 1 0 1.176H2.008l4.29 4.29a.589.589 0 0 1-.831.832L.172 20.416a.588.588 0 0 1 0-.832l5.295-5.294a.589.589 0 0 1 .831.832l-4.29 4.29z" />
          </svg>
        </button>

        <button
          aria-label="Next step"
          className={css.navigationButton}
          disabled={stepIndex === STEPS.length - 1}
          onClick={goToNextSlide}>
          <svg aria-hidden viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <path d="M37.992 19.412H.588a.588.588 0 0 0 0 1.176h37.404l-4.29 4.29a.589.589 0 0 0 .832.832l5.294-5.294a.588.588 0 0 0 0-.832l-5.294-5.294a.589.589 0 0 0-.832.832l4.29 4.29z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export const KitRegistrationInstructionModal = () => {
  const [stepIndex, setStepIndex] = useState<number>(0);

  return (
    <Modal
      layout="drawer"
      header={
        <Text
          variant="heading4"
          color="doveGray"
          className={sprinkles({flex: 'flex', h: 'full', items: 'center'})}>
          Step {stepIndex + 1} of {STEPS.length}
        </Text>
      }>
      <CarouselProvider>
        <StepsCarousel onStepIndexChange={setStepIndex} />
      </CarouselProvider>
    </Modal>
  );
};
